import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Tooltip } from './overlay';

export const buttonStyles = cva(
  [
    'inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    'transition-[background-color,border-color,color,box-shadow] duration-(--dur-fast)',
    'disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand text-on-brand shadow-xs hover:bg-brand-hover active:bg-brand-pressed',
        secondary: 'border border-border-strong bg-surface text-fg shadow-xs hover:bg-surface-2 active:bg-sunken',
        subtle: 'bg-brand-subtle text-brand-text hover:bg-brand-subtle-hover',
        ghost: 'text-fg hover:bg-sunken active:bg-border',
        danger: 'bg-danger-solid text-on-danger-solid shadow-xs hover:brightness-95 active:brightness-90',
        'danger-ghost': 'text-danger-fg hover:bg-danger-bg',
        link: 'h-auto rounded-sm px-0 text-brand-text underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-(--control-h-sm) px-2.5 text-sm',
        md: 'h-(--control-h) px-3.5 text-base',
        lg: 'h-(--control-h-lg) px-5 text-md',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  asChild?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, loading, leadingIcon, trailingIcon, disabled, children, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';
  const busyProps = loading ? ({ 'aria-busy': true } as const) : {};
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonStyles({ variant, size }), className)}
      disabled={disabled || loading}
      {...busyProps}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 className="animate-spin" aria-hidden /> : leadingIcon}
          {children}
          {!loading && trailingIcon}
        </>
      )}
    </Comp>
  );
});

const iconSize = {
  sm: 'size-(--control-h-sm)',
  md: 'size-(--control-h)',
  lg: 'size-(--control-h-lg)',
} as const;

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'>, VariantProps<typeof buttonStyles> {
  /** Required accessible name; also shown as a tooltip. */
  label: string;
  tooltip?: boolean;
  loading?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, tooltip = true, className, variant = 'ghost', size = 'md', loading, children, type, disabled, ...props },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      disabled={disabled || loading}
      className={cn(buttonStyles({ variant, size }), 'px-0', iconSize[size ?? 'md'], className)}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : children}
    </button>
  );
  return tooltip ? <Tooltip content={label}>{button}</Tooltip> : button;
});
